// Variable-type routing for extracted declarations.
// Example: `--sizes: [1,2]` -> arrayVariables map.
import type { LuisCompileWarning } from '../../types'
import type { LuisArrayVariables, LuisObjectVariables } from '../../types'
import type { ParsedVariableDeclaration } from './types'
import { hasMismatchedArrayRangeUnits, isArrayVariableValue, parseArrayVariable } from '../array-variable'
import { isClassVariableValue } from '../class-variable'
import { isObjectVariableValue, parseObjectVariable } from '../object-variable'
import { failClosedSecurityCheck } from '../../../security/guard'
import { hasUnsafeCssPayload } from '@lentystyle/guard/shared'
import type { LuisResourceLimits, LuisResourcePolicy } from '../../resource-limits'

// Route declaration values to regular, class, object, or array registries.
export function assignSystemVariable(
  name: string,
  value: string,
  variables: Record<string, string>,
  classVariables: Record<string, string>,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables,
  warnings: LuisCompileWarning[],
  collectWarnings: boolean,
  line: number,
  sourceId = '<inline>',
  resourcePolicy: LuisResourcePolicy = 'fail-closed',
  resourceLimits?: LuisResourceLimits,
): void {
  // Skip unsafe variable payloads before type routing.
  if (containsUnsafeVariablePayload(value)) {
    return
  } else {
    // Continue value-type routing for safe payloads.
  }

  // Route object-like values: `--obj: { key: value; }`.
  if (isObjectVariableValue(value)) {
    objectVariables[name] = parseObjectVariable(value, objectVariables, resourceLimits
      ? {
        variables,
        arrays: arrayVariables,
        objectVariables,
        sourceId: `${sourceId}::${name}`,
        resourcePolicy,
        resourceLimits,
        warnings,
        lineOffset: line,
      }
      : undefined)
    // Route array-like values: `--sizes: [1, 2, 3]`.
  } else if (isArrayVariableValue(value)) {
    // Warn when ranged array units mismatch like `[1px-12rem]`.
    if (collectWarnings && hasMismatchedArrayRangeUnits(value)) {
      warnings.push(buildArrayRangeUnitWarning(name, line, value))
    } else {
      // Continue array parsing when no mismatch warning is needed.
    }
    arrayVariables[name] = parseArrayVariable(value)
    // Route rule-like values: `--rule: .a { ... }`.
  } else if (isClassVariableValue(value)) {
    classVariables[name] = value
    // Keep plain CSS values under regular custom properties.
  } else {
    variables[name] = value
  }
}

// Assign all parsed declarations into variable registries.
export function assignDeclarations(
  declarations: ParsedVariableDeclaration[],
  variables: Record<string, string>,
  classVariables: Record<string, string>,
  objectVariables: LuisObjectVariables,
  arrayVariables: LuisArrayVariables,
  warnings: LuisCompileWarning[],
  collectWarnings: boolean,
  sourceId = '<inline>',
  resourcePolicy: LuisResourcePolicy = 'fail-closed',
  resourceLimits?: LuisResourceLimits,
): void {
  // Assign each declaration using value-type aware routing.
  for (const declaration of declarations) {
    assignSystemVariable(
      declaration.name,
      declaration.value,
      variables,
      classVariables,
      objectVariables,
      arrayVariables,
      warnings,
      collectWarnings,
      declaration.line ?? 1,
      sourceId,
      resourcePolicy,
      resourceLimits,
    )
  }
}

// Detect unsafe variable payloads to prevent css/script breakout values.
function containsUnsafeVariablePayload(value: string): boolean {
  try {
    // Reuse shared payload predicate before value-shape specific checks.
    if (hasUnsafeCssPayload(value)) {
      return true
    } else {
      // Continue structural token safety checks.
    }

    // Keep regular variable values free from structural braces.
    if (
      !isObjectVariableValue(value)
      && !isClassVariableValue(value)
      && !isArrayVariableValue(value)
      && (value.includes('{') || value.includes('}'))
    ) {
      return true
    } else {
      // Return false when payload passes unsafe checks.
      return false
    }
  } catch (error) {
    // Stop compile flow when variable payload sanitizer cannot execute.
    return failClosedSecurityCheck('containsUnsafeVariablePayload', error)
  }
}

// Build warning for mixed-unit one-token ranged arrays.
function buildArrayRangeUnitWarning(name: string, line: number, value: string): LuisCompileWarning {
  // Return warning payload without stopping compilation.
  return {
    code: 'invalid-array-range-unit',
    line,
    message: `Array range "${name}" uses mismatched units in "${value}". Prefer same-unit ranges like [1px-12px] or [1rem-12rem].`,
  }
}
